import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonGroup,
  Dropdown,
  Image,
  TableCell,
  TableRow,
} from "semantic-ui-react";
import {
  summariseCallRecording,
  transcribeCallRecording,
} from "../../api/transcriptions";
import sidekickLogoWhite from "../../images/sidekick-logo-white-75x79.png";
import { style } from "../../style";
import { SummariseTemplatePicker } from "./SummariseTemplatePicker";

const CallRecordingRow = ({
  id,
  recordingRef,
  number,
  start,
  end,
  extension,
  direction,
  duration,
  timezone,
  transcription,
  openCall,
  templates,
  searchParams,
  actionsDisabledReason,
}) => {
  const queryClient = useQueryClient();

  const [isProcessing, setIsProcessing] = useState(
    ["TRANSCRIBING", "SUMMARISING"].includes(transcription?.status?.value)
      ? true
      : false,
  );

  useEffect(() => {
    setIsProcessing(
      ["TRANSCRIBING", "SUMMARISING"].includes(transcription?.status?.value)
        ? true
        : false,
    );
  }, [transcription?.status?.value]);

  const handleError = (error) => {
    toast(
      error.response?.data?.message ?? "There was an error with your request",
      {
        type: "error",
        autoClose: 10000,
        toastId: "sidekick-limit-error",
      },
    );
  };

  return (
    <TableRow>
      <TableCell>{extension}</TableCell>
      <TableCell>{number}</TableCell>
      <TableCell>{direction}</TableCell>
      <TableCell>{start}</TableCell>
      <TableCell>{end}</TableCell>
      <TableCell>{timezone}</TableCell>
      <TableCell>{duration}</TableCell>
      <TableCell textAlign="right">
        <ActionButton
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
          transcription={transcription}
          openCall={openCall}
          templates={templates}
          searchParams={searchParams}
          queryClient={queryClient}
          id={id}
          recordingRef={recordingRef}
          handleError={handleError}
          actionsDisabledReason={actionsDisabledReason}
        />
      </TableCell>
    </TableRow>
  );
};

function ActionButton({
  isProcessing,
  setIsProcessing,
  transcription,
  openCall,
  templates: { templates, isLoading: templatesLoading },
  searchParams,
  queryClient,
  id,
  recordingRef,
  handleError,
  actionsDisabledReason,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleRequest = async (apiRequest, extraParams = {}) => {
    setIsProcessing(true);
    try {
      await apiRequest({
        slug: id,
        recording_ref: recordingRef,
        ...searchParams,
        ...extraParams,
      });
      queryClient.invalidateQueries({
        queryKey: ["call-recordings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["call-transcriptions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sidekick-minutes-usage"],
      });
      // A cached call page would otherwise show its old status for a minute
      queryClient.invalidateQueries({ queryKey: ["call-detail"] });
    } catch (e) {
      setIsProcessing(false);
      handleError(e);
    }
  };

  const handleAiClick = () => {
    if (!templatesLoading && templates.length === 0) {
      handleRequest(summariseCallRecording);
      return;
    }
    setPickerOpen(true);
  };

  const handleTranscribeClick = () => handleRequest(transcribeCallRecording);

  const handleConfirmTemplate = (templateId) => {
    setPickerOpen(false);
    handleRequest(summariseCallRecording, {
      meeting_notes_template_id: templateId,
    });
  };

  if (transcription || isProcessing) {
    return (
      <Button
        fluid
        style={{ ...style.secondaryButton, ...style.compactButton }}
        loading={isProcessing}
        disabled={isProcessing}
        onClick={() => openCall(transcription.id)}
      >
        View
      </Button>
    );
  }

  return (
    <>
      {/* Disabled buttons don't get hover events, so the tooltip sits on a wrapper. */}
      <span
        title={actionsDisabledReason ?? undefined}
        style={{ display: "block" }}
      >
        <ButtonGroup style={{ width: "100%" }}>
          <Button
            className="sk-gradient"
            style={{
              ...style.gradientButton,
              ...style.compactButton,
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
            onClick={handleAiClick}
            disabled={Boolean(actionsDisabledReason)}
          >
            <Image
              src={sidekickLogoWhite}
              alt="Sidekick logo"
              style={{ width: "14px", margin: 0 }}
            />
            <div>AI</div>
          </Button>

          <Dropdown
            style={{
              ...style.gradientButton,
              ...style.compactButton,
              padding: "7px 9px",
              flex: "none",
              // Carries on from the end of the AI button's gradient
              background: "#2fa7a2",
              borderLeft: "1px solid rgba(255, 255, 255, 0.25)",
            }}
            className="button icon sk-gradient"
            disabled={Boolean(actionsDisabledReason)}
            floating
            options={[
              {
                key: "transcribe",
                text: "Transcribe only",
                value: "transcribe",
                onClick: handleTranscribeClick,
              },
            ]}
            trigger={<></>}
          />
        </ButtonGroup>
      </span>
      {pickerOpen && (
        <SummariseTemplatePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={handleConfirmTemplate}
        />
      )}
    </>
  );
}

export default CallRecordingRow;
